/**
 * Custom Hook for Fetching Conversation History from API
 * Manages API state and fetches conversation history following SOLID principles
 * Uses React Query with no caching
 */

import { useQuery } from '@tanstack/react-query';
import { conversationHistoryService } from '@/services/chatHistoryService';
import { ConversationHistoryResponse } from '@/types/chatTypes';

interface UseConversationHistoryReturn {
  conversationHistory: ConversationHistoryResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook for fetching conversation history from the API using React Query
 * Configured with no caching (gcTime: 0, staleTime: 0)
 * @param user_id - The user ID to fetch history for
 * @returns Conversation history state and refetch function
 */
export const useConversationHistory = (
  user_id: string | null
): UseConversationHistoryReturn => {
  const {
    data: conversationHistory,
    isLoading,
    error,
    refetch,
  } = useQuery<ConversationHistoryResponse>({
    queryKey: ['conversationHistory', user_id],
    queryFn: async () => {
      if (!user_id) {
        throw new Error('User ID is required');
      }
      return conversationHistoryService.fetchConversationHistory({
        user_id,
      });
    },
    enabled: !!user_id, // Only run query if user_id is available
    gcTime: 0, // No cache time - data is immediately garbage collected
    staleTime: 0, // Data is immediately considered stale - always refetch
    retry: 1, // Retry once on failure
  });

  return {
    conversationHistory: conversationHistory || null,
    isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : 'Failed to fetch conversation history'
      : null,
    refetch,
  };
};
